import { Loader } from 'components/ai-elements/loader';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { captureArtifactError } from 'sentry/artifact';

import { RENDERER_SHELL_HTML } from './jsx-dashboard-shell';

type JSXDashboardProps = {
  url: string;
};

/**
 * Renders a JSX module dashboard generated by the chatbot.
 * Uses srcdoc to give the iframe an opaque origin, preventing access to
 * parent cookies, localStorage, and Auth0 tokens. The shell HTML is inlined
 * as a string constant to avoid needing allow-same-origin on the sandbox.
 *
 * The iframe fills its container and scrolls internally. Dynamic height sizing
 * was removed because content using viewport units (e.g. min-h-screen) created
 * a feedback loop: iframe grows → 100vh grows → scrollHeight grows → iframe grows.
 *
 * TODO: Move renderer shell to a separate origin (e.g. sandbox.oilytics.co)
 * for defense-in-depth — srcdoc is safe but a dedicated origin adds CSP
 * isolation and makes security auditing easier.
 */
export const JSXDashboard = ({ url }: JSXDashboardProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [jsx, setJsx] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Fetch JSX source from S3
  useEffect(() => {
    let cancelled = false;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load dashboard: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setJsx(text);
      })
      .catch((err) => {
        if (cancelled) return;
        captureArtifactError(err, { url, kind: 'jsx' });
        setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Listen for messages from the renderer iframe
  const handleMessage = useCallback((event: MessageEvent) => {
    const iframe = iframeRef.current;
    if (!iframe || event.source !== iframe.contentWindow) return;

    switch (event.data?.type) {
    case 'ready':
      setIframeReady(true);
      break;
    case 'error': {
      const message = event.data.message || 'Unknown render error';
      // Real user-facing JSX runtime crashes — capture with the artifact URL
      // so we can correlate to the specific generated artifact.
      captureArtifactError(new Error(message), {
        url,
        kind: 'jsx-render',
        extra: {
          stack: event.data.stack,
          line: event.data.line,
          column: event.data.column,
        },
      });
      setError(message);
      break;
    }
    default:
      break;
    }
  }, [url]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Send JSX to iframe once both the code is fetched and the iframe is ready
  useEffect(() => {
    if (jsx && iframeReady && iframeRef.current?.contentWindow) {
      setError(null);
      // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration -- sandboxed iframe (allow-scripts only) has an opaque origin, so '*' is the only usable target; it receives the user's own dashboard code
      iframeRef.current.contentWindow.postMessage(
        { type: 'render', code: jsx },
        '*'
      );
    }
  }, [jsx, iframeReady]);

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (jsx === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-muted overflow-hidden bg-white relative h-full">
      <iframe
        ref={iframeRef}
        srcDoc={RENDERER_SHELL_HTML}
        sandbox="allow-scripts"
        title="JSX Dashboard"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
        }}
      />
    </div>
  );
};
